import React, { useMemo } from 'react';
import styles from './index.module.scss';
import Card from '@components-clean/core/card';
import CardAction from '@components-clean/core/card/card-actions';
import ChipList from '@components-clean/core/chip-list';
import { InfoField } from '@components-clean/core/info-field';
import Loader from '@components-clean/core/loader';
import AddEditSubjectTagsDialog from '@pages-clean/subject-monitoring/subject-hub/components/dialogs/add-edit-subject-tags-dialog';
import { useBoolean, useTranslation } from '@hooks/index';
import { SubjectMonitoringAPI } from '@apis/bff-api/subject-monitoring/subject-profile';
import { TagAPI } from '@apis/bff-api/tag';

type Props = {
    canEdit: boolean;
    studyRef: string;
    datasetId: number;
    subjectCode: string;
};

const SubjectProfileDetailsTagsCard = ({ canEdit, studyRef, datasetId, subjectCode }: Props) => {
    const { t: tCommon } = useTranslation('common');
    const { t: tSm } = useTranslation('sm');

    const showEditTagsDialog = useBoolean(false);

    const getAllTagsQuery = TagAPI.useGetAllTagsQuery(studyRef);
    const getSubjectSelectedTagsQuery = SubjectMonitoringAPI.useGetSelectedTagsForSubjectQuery(studyRef, subjectCode);

    const selectedActiveTags = useMemo(() => {
        const selectedIdsSet = new Set(getSubjectSelectedTagsQuery.data);

        const mappedTags = (getAllTagsQuery.data ?? [])
            // Filter out any which don't have expected shape, and map to type where everything isn't nullable...
            .filter((t) => typeof t.id === 'number' && typeof t.label === 'string' && typeof t.active === 'boolean')
            .map((t) => ({
                id: t.id!,
                value: t.label!,
                active: t.active!,
            }))
            // Sort alphabetically asc
            .sort((a, z) =>
                a.value.localeCompare(z.value, undefined, {
                    numeric: true,
                    sensitivity: 'base',
                }),
            );

        return mappedTags.filter((t) => selectedIdsSet.has(t.id));
    }, [getAllTagsQuery.data, getSubjectSelectedTagsQuery.data]);

    const isLoading = getAllTagsQuery.isLoading || getSubjectSelectedTagsQuery.isLoading;

    const isError = !!getAllTagsQuery.error || !!getSubjectSelectedTagsQuery.error;

    return (
        <>
            <Card
                data-testid="subject-profile-details-tags-card"
                title={tSm('subjectTagDialog.tagsLabel')}
                headerActions={
                    canEdit && (
                        <CardAction
                            data-testid="edit-subject-tags"
                            className="k-mr-2.5"
                            faIcon={['far', 'edit']}
                            onClick={showEditTagsDialog.setTrue}
                        />
                    )
                }
                collapsible
            >
                {isLoading || isError ? (
                    <div className={styles.cardLoadingStateWrapper}>
                        {isError ? (
                            <InfoField type="Error" message={tCommon('errorMessage')} />
                        ) : isLoading ? (
                            <Loader />
                        ) : null}
                    </div>
                ) : (
                    <ChipList
                        data-testid="subject-tags-list"
                        selection="none"
                        activeField="active"
                        data={selectedActiveTags.map((t) => ({
                            key: t.id,
                            text: t.value,
                            active: t.active,
                        }))}
                    />
                )}
            </Card>

            {canEdit && showEditTagsDialog.value && (
                <AddEditSubjectTagsDialog
                    isEditSubject
                    isSubjectProfile
                    studyRef={studyRef}
                    datasetId={datasetId}
                    subjectCodes={[subjectCode]}
                    onClose={showEditTagsDialog.setFalse}
                    onSubmit={async () => {
                        showEditTagsDialog.setFalse();
                        // Refetch queries
                        await Promise.allSettled([getAllTagsQuery.mutate(), getSubjectSelectedTagsQuery.mutate()]);
                    }}
                />
            )}
        </>
    );
};

export default SubjectProfileDetailsTagsCard;
